import { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { ReactComponent as DirectionLtrIcon } from "../../assets/icons/direction_ltr.svg";
import { ReactComponent as DirectionRtlIcon } from "../../assets/icons/direction_rtl.svg";
import { ReactComponent as DoublePageIcon } from "../../assets/icons/double_page.svg";
import { ReactComponent as FontDownIcon } from "../../assets/icons/FontDown.svg";
import { ReactComponent as FontUpIcon } from "../../assets/icons/FontUp.svg";
import { ReactComponent as MinusCircleIcon } from "../../assets/icons/minus-circle.svg";
import { ReactComponent as PlusCircleIcon } from "../../assets/icons/plus-circle.svg";
import { ReactComponent as RotateLeftIcon } from "../../assets/icons/rotate_left.svg";
import { ReactComponent as RotateRightIcon } from "../../assets/icons/rotate_right.svg";
import { ReactComponent as SinglePageIcon } from "../../assets/icons/single_page.svg";
import { ReactComponent as ZoomResetIcon } from "../../assets/icons/zoomReset.svg";
import useReaderStore from "../../context/readerStore";
import {
  useIsMobile,
  useIsTablet,
  useIsTouchTablet,
} from "../../hooks/useMediaQuery";
import {
  EPUB_FONT_OPTIONS,
  EPUB_LINE_HEIGHT_MAX,
  EPUB_LINE_HEIGHT_MIN,
  getEpubFontFamily,
  isEpubOnlyTheme,
  READER_THEME_SWATCHES,
  stepEpubLineHeight,
} from "../../utils/epubThemes";
import "./DisplayMenu.css";

const MenuIconButton = ({
  label,
  selected = false,
  disabled = false,
  onClick,
  children,
  radio = false,
}) => (
  <button
    type="button"
    role={radio ? "menuitemradio" : "menuitem"}
    aria-checked={radio ? selected : undefined}
    className={`display-menu-icon-btn${selected ? " is-selected" : ""}${
      disabled ? " is-disabled" : ""
    }`}
    aria-label={label}
    title={label}
    disabled={disabled}
    onClick={onClick}
  >
    {children}
  </button>
);

const DisplayMenu = ({ isOpen, onClose, anchorRef }) => {
  const { t } = useTranslation();
  const menuRef = useRef(null);

  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const isTouchTablet = useIsTouchTablet();
  const canUseDoublePage = !isMobile && !isTablet && !isTouchTablet;

  const {
    theme,
    setTheme,
    pageViewMode,
    setPageViewMode,
    setReaderViewMode,
    documentType,
    scale,
    setScale,
    setPdfRotation,
    readingDirection,
    setReadingDirection,
    epubFontFamily,
    setEpubFontFamily,
    epubLineHeight,
    setEpubLineHeight,
  } = useReaderStore();

  const showLayoutControls =
    (documentType === "pdf" || documentType === "epub") && canUseDoublePage;
  const showScaleControls =
    documentType === "pdf" || documentType === "epub";
  const isEpub = documentType === "epub";
  // Mint/sepia are EPUB reading surfaces — hide for PDF.
  const colorModes = READER_THEME_SWATCHES.filter(
    (mode) => isEpub || !mode.epubOnly,
  );

  const minScale = isEpub
    ? isTouchTablet
      ? 1.0
      : 0.75
    : isTouchTablet
      ? 1.0
      : 0.5;
  const maxScale = isEpub ? 2.0 : 5.0;

  // Mint/sepia are EPUB-only. Coerce only when we know the doc is PDF —
  // do not treat documentType === null (initial load) as PDF or mint is wiped
  // before an EPUB finishes loading.
  useEffect(() => {
    if (documentType === "pdf" && isEpubOnlyTheme(theme)) {
      setTheme("light");
    }
  }, [documentType, theme, setTheme]);

  const handleSelectTheme = useCallback(
    (selectedTheme) => {
      if (documentType === "pdf" && isEpubOnlyTheme(selectedTheme)) return;
      setTheme(selectedTheme);
      onClose();
    },
    [documentType, setTheme, onClose],
  );

  const handleSelectPageViewMode = useCallback(
    (mode) => {
      setPageViewMode(mode);
      if (mode === "double" && documentType === "epub") {
        setReaderViewMode("default");
      }
      if (mode === "single" && documentType === "epub") {
        setReaderViewMode("fit-to-width");
      }
      onClose();
    },
    [setPageViewMode, setReaderViewMode, documentType, onClose],
  );

  const handleZoomIn = useCallback(() => {
    if (documentType !== "pdf" && documentType !== "epub") return;
    const step = documentType === "epub" ? 0.2 : 0.1;
    setScale(Math.min(maxScale, scale + step));
    if (documentType === "pdf") {
      setReaderViewMode("default");
    }
  }, [scale, setScale, setReaderViewMode, documentType, maxScale]);

  const handleZoomOut = useCallback(() => {
    if (documentType !== "pdf" && documentType !== "epub") return;
    setScale(Math.max(minScale, scale - 0.1));
    if (documentType === "pdf") {
      setReaderViewMode("default");
    }
  }, [scale, setScale, setReaderViewMode, documentType, minScale]);

  const handleZoomReset = useCallback(() => {
    if (documentType !== "pdf" && documentType !== "epub") return;

    if (documentType === "epub") {
      setScale(1.0);
      setReaderViewMode("fit-to-width");
      return;
    }

    // Mobile PDF opens in fit-to-width; reset should restore that, not 100%.
    if (isMobile) {
      setReaderViewMode("fit-to-width");
      window.dispatchEvent(new CustomEvent("reader:fit-to-width"));
      return;
    }

    setScale(1.0);
    setReaderViewMode("default");
  }, [setScale, setReaderViewMode, documentType, isMobile]);

  // PDF.js pagesRotation is clockwise in 90° steps: 0 → 90 → 180 → 270 → 0.
  const handleRotate = useCallback(
    (deltaDegrees) => {
      if (documentType !== "pdf") return;
      setPdfRotation((prev) => (Number(prev) || 0) + deltaDegrees);
    },
    [documentType, setPdfRotation],
  );

  const handleSelectReadingDirection = useCallback(
    (direction) => {
      if (documentType !== "epub") return;
      setReadingDirection(direction);
    },
    [documentType, setReadingDirection],
  );

  const handleSelectEpubFont = useCallback(
    (fontId) => {
      if (documentType !== "epub") return;
      setEpubFontFamily(fontId);
    },
    [documentType, setEpubFontFamily],
  );

  const handleLineHeightStep = useCallback(
    (direction) => {
      if (documentType !== "epub") return;
      setEpubLineHeight(stepEpubLineHeight(epubLineHeight, direction));
    },
    [documentType, epubLineHeight, setEpubLineHeight],
  );

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleOutsidePointer = (event) => {
      const target = event.target;
      if (menuRef.current?.contains(target)) return;
      if (anchorRef?.current?.contains(target)) return;
      onClose();
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };

    document.addEventListener("pointerdown", handleOutsidePointer, true);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handleOutsidePointer, true);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose, anchorRef]);

  if (!isOpen) return null;

  const increaseLabel = isEpub
    ? t("rightToolbar.increaseFontSize")
    : t("rightToolbar.zoomIn");
  const decreaseLabel = isEpub
    ? t("rightToolbar.decreaseFontSize")
    : t("rightToolbar.zoomOut");
  const resetLabel = isEpub
    ? t("rightToolbar.resetFontSize")
    : t("rightToolbar.resetZoom");
  const scaleSectionLabel = isEpub
    ? t("toolbar.displayMenuItems.fontSize")
    : t("help.zoomControls");

  return (
    <div
      ref={menuRef}
      className="display-menu"
      role="menu"
      aria-label={t("toolbar.displayMenu")}
    >
      <section className="display-menu-section">
        <h4 className="display-menu-label">
          {t("toolbar.displayMenuItems.colorMode")}
        </h4>
        <div className="display-menu-row">
          {colorModes.map((mode) => {
            const label = t(`toolbar.displayMenuItems.colors.${mode.id}`);
            const selected = theme === mode.id;
            return (
              <button
                key={mode.id}
                type="button"
                role="menuitemradio"
                aria-checked={selected}
                className={`display-menu-swatch${
                  selected ? " is-selected" : ""
                }`}
                style={{ backgroundColor: mode.color }}
                aria-label={label}
                title={label}
                onClick={() => handleSelectTheme(mode.id)}
              />
            );
          })}
        </div>
      </section>

      {showScaleControls && (
        <section className="display-menu-section">
          <h4 className="display-menu-label">{scaleSectionLabel}</h4>
          <div className="display-menu-row">
            <MenuIconButton
              disabled={scale >= maxScale}
              label={increaseLabel}
              onClick={handleZoomIn}
            >
              {isEpub ? (
                <FontUpIcon aria-hidden="true" />
              ) : (
                <PlusCircleIcon aria-hidden="true" />
              )}
            </MenuIconButton>
            <MenuIconButton
              disabled={scale <= minScale}
              label={decreaseLabel}
              onClick={handleZoomOut}
            >
              {isEpub ? (
                <FontDownIcon aria-hidden="true" />
              ) : (
                <MinusCircleIcon aria-hidden="true" />
              )}
            </MenuIconButton>
            <MenuIconButton label={resetLabel} onClick={handleZoomReset}>
              <ZoomResetIcon aria-hidden="true" />
            </MenuIconButton>
          </div>
        </section>
      )}

      {isEpub && (
        <section className="display-menu-section">
          <h4 className="display-menu-label">
            {t("toolbar.displayMenuItems.fontFamily")}
          </h4>
          <div className="display-menu-row display-menu-row--wrap">
            {EPUB_FONT_OPTIONS.map((font) => {
              const label = t(`toolbar.displayMenuItems.fonts.${font.id}`);
              const selected = epubFontFamily === font.id;
              const previewFamily =
                getEpubFontFamily(font.id) || "inherit";
              return (
                <button
                  key={font.id}
                  type="button"
                  role="menuitemradio"
                  aria-checked={selected}
                  className={`display-menu-font-btn${
                    selected ? " is-selected" : ""
                  }`}
                  style={{ fontFamily: previewFamily }}
                  aria-label={label}
                  title={label}
                  onClick={() => handleSelectEpubFont(font.id)}
                >
                  <span className="display-menu-font-preview" aria-hidden="true">
                    Aa
                  </span>
                  <span className="display-menu-font-name">{label}</span>
                </button>
              );
            })}
          </div>
        </section>
      )}

      {isEpub && (
        <section className="display-menu-section">
          <h4 className="display-menu-label">
            {t("toolbar.displayMenuItems.lineHeight")}
          </h4>
          <div className="display-menu-row">
            <MenuIconButton
              disabled={epubLineHeight <= EPUB_LINE_HEIGHT_MIN}
              label={t("toolbar.displayMenuItems.decreaseLineHeight")}
              onClick={() => handleLineHeightStep(-1)}
            >
              <MinusCircleIcon aria-hidden="true" />
            </MenuIconButton>
            <span className="display-menu-value" aria-hidden="true">
              {epubLineHeight.toFixed(2)}
            </span>
            <MenuIconButton
              disabled={epubLineHeight >= EPUB_LINE_HEIGHT_MAX}
              label={t("toolbar.displayMenuItems.increaseLineHeight")}
              onClick={() => handleLineHeightStep(1)}
            >
              <PlusCircleIcon aria-hidden="true" />
            </MenuIconButton>
          </div>
        </section>
      )}

      {showLayoutControls && (
        <section className="display-menu-section">
          <h4 className="display-menu-label">
            {t("toolbar.displayMenuItems.layout")}
          </h4>
          <div className="display-menu-row">
            <MenuIconButton
              radio
              selected={pageViewMode === "single"}
              label={t("toolbar.singlePage")}
              onClick={() => handleSelectPageViewMode("single")}
            >
              <SinglePageIcon aria-hidden="true" />
            </MenuIconButton>
            <MenuIconButton
              radio
              selected={pageViewMode === "double"}
              label={t("toolbar.doublePage")}
              onClick={() => handleSelectPageViewMode("double")}
            >
              <DoublePageIcon aria-hidden="true" />
            </MenuIconButton>
          </div>
        </section>
      )}

      {documentType === "pdf" && (
        <section className="display-menu-section">
          <h4 className="display-menu-label">
            {t("toolbar.displayMenuItems.rotate")}
          </h4>
          <div className="display-menu-row">
            <MenuIconButton
              label={t("toolbar.displayMenuItems.rotateRight")}
              onClick={() => handleRotate(90)}
            >
              <RotateRightIcon aria-hidden="true" />
            </MenuIconButton>
            <MenuIconButton
              label={t("toolbar.displayMenuItems.rotateLeft")}
              onClick={() => handleRotate(-90)}
            >
              <RotateLeftIcon aria-hidden="true" />
            </MenuIconButton>
          </div>
        </section>
      )}

      {/* Reading direction: desktop/tablet for now. Viewer already supports it on
          mobile — re-enable this section when product wants the control there. */}
      {isEpub && !isMobile && (
        <section className="display-menu-section">
          <h4 className="display-menu-label">
            {t("toolbar.displayMenuItems.readingDirection")}
          </h4>
          <div className="display-menu-row">
            <MenuIconButton
              radio
              selected={readingDirection === "ltr"}
              label={t("toolbar.displayMenuItems.ltr")}
              onClick={() => handleSelectReadingDirection("ltr")}
            >
              <DirectionLtrIcon aria-hidden="true" />
            </MenuIconButton>
            <MenuIconButton
              radio
              selected={readingDirection === "rtl"}
              label={t("toolbar.displayMenuItems.rtl")}
              onClick={() => handleSelectReadingDirection("rtl")}
            >
              <DirectionRtlIcon aria-hidden="true" />
            </MenuIconButton>
          </div>
        </section>
      )}
    </div>
  );
};

export default DisplayMenu;
